import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { PublicNav } from "@/components/public/PublicNav";
import { PublicFooter } from "@/components/public/PublicFooter";
import { useLang } from "@/i18n/LanguageContext";
import {
  Smartphone, BadgePercent, CreditCard, Palette, Users, Rocket,
  ArrowRight, Check, X,
} from "lucide-react";

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, delay: d, ease: [0.22, 1, 0.36, 1] },
});

const featureIcons = [Smartphone, BadgePercent, CreditCard, Palette, Users, Rocket];

export default function Landing() {
  const { t } = useLang();
  const L = t.landing;
  return (
    <div className="min-h-screen bg-background text-foreground font-adminbody">
      <PublicNav />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <motion.div {...fade(0)} className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-6">
              {L.eyebrow}
            </motion.div>
            <motion.h1 {...fade(0.06)} className="font-admin font-extrabold tracking-tight leading-[1.02] text-5xl sm:text-6xl lg:text-[4.25rem]">
              {L.h1[0]}<br />{L.h1[1]}
            </motion.h1>
            <motion.p {...fade(0.14)} className="mt-7 text-lg text-muted-foreground max-w-lg leading-relaxed">
              {L.heroDesc}
            </motion.p>
            <motion.div {...fade(0.22)} className="mt-9 flex flex-wrap gap-3">
              <Link to="/contacto">
                <Button size="lg" className="h-12 px-7 rounded-full text-base font-semibold gap-2" data-testid="hero-request-btn">
                  {L.ctaPrimary} <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <a href="#comparativa">
                <Button size="lg" variant="outline" className="h-12 px-7 rounded-full text-base font-semibold border-border" data-testid="hero-learn-btn">
                  {L.ctaSecondary}
                </Button>
              </a>
            </motion.div>
          </div>

          <motion.div {...fade(0.16)} className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl shadow-neutral-300/60" style={{ backgroundColor: "#fdf2e5" }}>
              <img src="/hero-promo.png" alt="Domifood" className="w-full h-[520px] object-contain" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Comparison 30% vs 7% */}
      <section id="comparativa" className="bg-neutral-50">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-24">
          <motion.h2 {...fade(0)} className="font-admin font-extrabold text-3xl sm:text-4xl tracking-tight mb-3 max-w-3xl">{L.compareTitle}</motion.h2>
          <motion.p {...fade(0.05)} className="text-muted-foreground mb-12 max-w-2xl text-lg">{L.compareSub}</motion.p>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Others */}
            <motion.div {...fade(0.05)} className="rounded-3xl bg-background p-8 ring-1 ring-black/5">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-admin font-bold text-xl text-neutral-500">{L.otherTitle}</h3>
                <span className="font-admin font-extrabold text-4xl text-neutral-400">30%</span>
              </div>
              <ul className="space-y-3">
                {L.otherPoints.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-neutral-200 flex items-center justify-center flex-shrink-0"><X className="w-3 h-3 text-neutral-500" /></span>
                    {p}
                  </li>
                ))}
              </ul>
            </motion.div>
            {/* Domifood */}
            <motion.div {...fade(0.12)} className="rounded-3xl bg-neutral-950 text-neutral-100 p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-admin font-bold text-xl flex items-center gap-2">
                  <img src="/domifood-logo.png" alt="Domifood" className="h-6 w-auto" />
                </h3>
                <span className="font-admin font-extrabold text-4xl">7%</span>
              </div>
              <ul className="space-y-3">
                {L.domiPoints.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm text-neutral-200">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0"><Check className="w-3 h-3 text-white" /></span>
                    {p}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features / benefits */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-24">
        <motion.h2 {...fade(0)} className="font-admin font-extrabold text-3xl sm:text-4xl tracking-tight mb-14">{L.featuresTitle}</motion.h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
          {L.features.map((f, i) => {
            const Icon = featureIcons[i] || Smartphone;
            return (
              <motion.div key={f.title} {...fade(0.05 * i)} className="flex flex-col gap-3">
                <div className="w-11 h-11 rounded-xl bg-neutral-50 flex items-center justify-center ring-1 ring-black/5">
                  <Icon className="w-5 h-5 text-foreground" />
                </div>
                <h4 className="font-admin font-bold text-lg">{f.title}</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pb-24">
        <motion.div {...fade(0)} className="rounded-3xl bg-neutral-950 text-neutral-100 p-12 md:p-16 text-center">
          <h2 className="font-admin font-extrabold text-3xl sm:text-5xl tracking-tight">{L.ctaTitle}</h2>
          <p className="mt-4 text-neutral-400 max-w-xl mx-auto text-lg">{L.ctaDesc}</p>
          <div className="mt-9 flex justify-center">
            <Link to="/contacto">
              <Button size="lg" className="h-12 px-8 rounded-full bg-background text-foreground hover:bg-neutral-100 font-semibold gap-2" data-testid="cta-contact-btn">
                {L.ctaContact} <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </motion.div>
      </section>

      <PublicFooter />
    </div>
  );
}
